/**
 * Copyright © 2026 Adnuntius AS.
 */
import {SignalResourceListBindingService} from '../common/controller/signal-resource-list-binding.service';
import {ChangeDetectionStrategy, Component, DestroyRef, Inject, Input, OnInit, inject} from '@angular/core';
import * as _ from 'lodash';

import template from './line-item-list-page.component.html';
import {
  SignalAdminListColumn,
  SignalAdminResourceListState,
  SignalAdminResourceListStateFactory
} from '../common/controller/signal-admin-resource-list-state';
import {getListChildren} from '../common/controller/resource-list-utils';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {LineItemBulkActionService} from './line-item-bulk-action.service';
import {LineItemListSetupService, LineItemListView} from './line-item-list-setup.service';
import {TargetingHelper} from '../targeting/targeting-helper';
import {ADN_TARGETING_TYPES, ALL_TARGETING_TYPES} from '../targeting/targeting-constants';
import {TargetingEditorModalService} from '../targeting/targeting-editor-modal.service';
import {CreativeApiService} from '../creative/creative-api.service';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LineItemApiService, LineItemModel} from './line-item-api.service';
import {apiInstantFromPickerDateBoundary} from '../common/locale/network-date-picker-utils';
import {getCountryName} from '../common/directives/country-select/countries';

@Component({
  standalone: false,
  selector: 'adn-line-item-list-page',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LineItemListPageComponent implements OnInit {
  private readonly listLifetime = inject(DestroyRef);
  @Input() parentObject: string;
  @Input() parentId: string;
  @Input() list: SignalAdminResourceListState<LineItemModel>;
  @Input()
  set readOnly(value: boolean) {
    (this.list || this.defaultList).readOnly.set(value);
  }

  readonly itemStateName = 'app.adv.line-items.line-item';
  readonly advertiserItemStateName = 'app.adv.advertisers.advertiser';
  readonly orderItemStateName = 'app.adv.orders.order';
  readonly tierItemStateName = 'app.adm.tiers.tier';
  readonly userItemStateName = 'app.adm.users.user';
  readonly creativeItemStateName = 'app.adv.creatives.creative';
  readonly layoutItemStateName = 'app.adm.layouts.layout';

  readonly childColumns: SignalAdminListColumn[] = [
    {param: 'name'},
    {param: '', label: 'layout', sortable: false},
    {param: 'width', label: 'dimensions', sortable: false},
    {param: '', label: 'info', sortable: false},
    {param: 'updateTime'},
    {param: '', label: 'targeting', sortable: false}
  ];
  readonly childHost: any = {};
  readonly selector = {outputFields: [] as any[], selFields: [] as string[]};
  readonly filterModel: any = {items: []};

  private readonly api = inject(LineItemApiService);
  private readonly defaultList = inject(SignalAdminResourceListStateFactory).create<LineItemModel>(
    this.api,
    {
      listId: 'fullLineItemList',
      baseTable: 'lineItem',
      nullsAsOldestParams: ['startDate', 'endDate'],
      transformResults: (items) => {
        this.lineItemListSetupService.enrichList({items});
        return items;
      }
    },
    inject(DestroyRef)
  );

  constructor(
    @Inject(LocalNetworkProfile) private readonly localNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly localUserPermissions: any,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    listBinding: SignalResourceListBindingService,
    @Inject(CreativeApiService) private readonly Creative: CreativeApiService,
    private readonly targetingEditorModalService: TargetingEditorModalService,
    private readonly lineItemBulkActionService: LineItemBulkActionService,
    private readonly lineItemListSetupService: LineItemListSetupService
  ) {
    this.list = this.defaultList;
    getListChildren(this.childHost, listBinding, OBJECT_TYPE.LineItem, this.Creative, this.listLifetime);
  }

  ngOnInit(): void {
    this.configureFilters(this.parentObject === 'Order' ? 'ORDER_VIEW' : 'LINE_ITEM_VIEW');
    this.list.load();
  }

  isFieldVisible(param: string): boolean {
    const selected = this.selector.selFields;
    const fields = selected.length
      ? selected
      : (this.filterModel.defaultFields || []).map((field: any) => this.fieldId(field));
    return fields.includes(param);
  }

  get visibleColumns(): SignalAdminListColumn[] {
    const fields = this.selector.outputFields.length
      ? this.selector.outputFields
      : this.filterModel.defaultFields || [];
    return this.normalizeColumns(fields);
  }

  filtersResolved(): boolean {
    return !this.list.loading();
  }

  toggleChildren(lineItem: any): void {
    this.childHost.getChildren?.(lineItem);
  }

  childList(lineItemId: string): any {
    return this.childHost.children?.[lineItemId];
  }

  businessLinkDisabled(): boolean {
    return this.filterModel.perms?.MANAGE_BUSINESS !== true;
  }

  userLinkDisabled(): boolean {
    const perms = this.filterModel.perms || {};
    return perms.MANAGE_SYSTEM !== true
      && perms.AX_ADVERTISER_ADMIN !== true
      && perms.AX_ADVERTISER_TEAM_ADMIN !== true;
  }

  systemLinkDisabled(): boolean {
    return this.filterModel.perms?.MANAGE_SYSTEM !== true;
  }

  layoutLinkDisabled(): boolean {
    return this.filterModel.perms?.MANAGE_LAYOUTS !== true;
  }

  eCpmValue(lineItem: any): any {
    const amount = _.get(lineItem, ['spendDelivery', 'amount']);
    const currency = _.get(lineItem, ['spendDelivery', 'currency']);
    const rendered = lineItem?.renderedDelivery;
    return _.isFinite(amount) && rendered ? {amount: amount / rendered * 1000, currency} : undefined;
  }

  cpcValue(lineItem: any): any {
    const amount = _.get(lineItem, ['spendDelivery', 'amount']);
    const currency = _.get(lineItem, ['spendDelivery', 'currency']);
    const clicks = lineItem?.clickDelivery;
    return _.isFinite(amount) && clicks ? {amount: amount / clicks, currency} : undefined;
  }

  openBulkActions(): void {
    this.lineItemBulkActionService.createBulkAction({
      bulkSelection: this.list.selected(),
      miscBulkAction: (modal: any, controller: any, items: Partial<LineItemModel>[], _resource: any, params: any) => {
        this.api.bulkSave(params || {}, items).subscribe({
          next: () => {
            modal.close();
            this.list.deselectAll();
            this.list.load();
          },
          error: () => {
            controller.form = controller.form || {};
            controller.form.bulkError = true;
          }
        });
      }
    })();
  }

  openBulkTargeting(): void {
    const targetIds = _.map(_.filter(ADN_TARGETING_TYPES, (targetType: any) => !targetType.bulkBlocked), 'id');
    this.targetingEditorModalService.open({
      lineItemType: 'NORMAL',
      modelLineItem: {},
      pageType: 'bulk',
      reachEnabled: 'LINE_ITEM',
      mandatoryTargets: undefined,
      overridableTargeting: undefined,
      availableTargets: _.pick(ADN_TARGETING_TYPES, targetIds),
      targetingMode: null,
      targeting: {},
      isLockedDown: false
    }).then((workingCopy: any) => {
      if (!workingCopy) {
        return;
      }
      const change = this.buildBulkTargetingChange(workingCopy);
      if (!change) {
        return;
      }
      this.api.bulkSave(this.buildBulkTargetingParams(workingCopy, change.updateProps), change.lineItems)
        .subscribe(() => {
          this.list.deselectAll();
          this.list.load();
        });
    });
  }

  private buildBulkTargetingParams(workingCopy: any, updateProps: string[]): any {
    const params: any = {
      ignoreProduct: this.localNetworkProfile.getDefaults()['defaultIgnoreMarketplaceTargeting']
    };
    if (!workingCopy.reset) {
      params.updateOnlyTheseTargetTypes = updateProps;
    }
    return params;
  }

  private buildBulkTargetingChange(workingCopy: any): {lineItems: any[]; updateProps: string[]} | null {
    const newTargeting = workingCopy.wc || {};
    if (workingCopy.reset) {
      return {
        updateProps: [],
        lineItems: Object.keys(this.list.selected()).map((id) => ({id, targeting: newTargeting}))
      };
    }
    const currentTargets = TargetingHelper.getTargetsWithTargets(workingCopy, ['wc']);
    if (_.isEmpty(currentTargets)) {
      return null;
    }
    const apiProps = _.uniq(_.map(currentTargets, (target: any) => target.targets));
    const updateProps = _.uniq(_.map(currentTargets, (target: any) => target.apiType));
    const changedTargeting = _.pick(newTargeting, apiProps);
    return {
      updateProps,
      lineItems: Object.keys(this.list.selected()).map((id) => ({id, targeting: changedTargeting}))
    };
  }

  private configureFilters(view: LineItemListView): void {
    this.filterModel.items = [];
    this.filterModel.perms = this.localUserPermissions.getAllVisPermissions();
    this.lineItemListSetupService.configureList(this.filterModel, view);
    this.filterModel.startDateFilter = {dir: '>=', startDate: null, workingStartDate: null};
    this.filterModel.endDateFilter = {dir: '<=', endDate: null, workingEndDate: null};
    this.filterModel.selectIn = this.list.selectIn;
    ['userState', 'executionState', 'source'].forEach((field) => {
      this.filterModel[field + 'Working'] = [...((this.list as any)[field + 'Working'] || [])];
    });
    this.filterModel.makeSelectInQuery = (field: string, values: string[] | null, maxCount?: number) => {
      this.list.makeSelectInQuery(field, values, maxCount);
      this.filterModel[field + 'Working'] = [...((this.list as any)[field + 'Working'] || [])];
    };
    this.filterModel.makeDateQuery = (clear = false) => this.applyDateFilter(clear);
    this.filterModel.makeTargetingQuery = (clear = false) => {
      this.filterModel.targetType = clear ? null : this.filterModel.workingTargetType;
      const targeting = this.filterModel.targetType
        ? ALL_TARGETING_TYPES[this.filterModel.targetType]?.apiType
        : null;
      this.list.setFilterParams('targeting', targeting ? {targeting} : {});
    };
    this.filterModel.getOrderName = () => this.filterModel.order?.name;
    this.filterModel.getTeamName = () => this.filterModel.team?.name;
    this.filterModel.getCountryName = () => getCountryName(this.filterModel.countryTeam);
    this.filterModel.makeOrderQuery = (clear = false) => {
      this.filterModel.order = clear ? null : this.filterModel.workingOrder;
      if (!clear) {
        this.filterModel.team = null;
        this.filterModel.workingTeam = null;
      }
      this.applyRelationshipFilters();
    };
    this.filterModel.makeTeamQuery = (clear = false) => {
      this.filterModel.team = clear ? null : this.filterModel.workingTeam;
      if (!clear) {
        this.filterModel.order = null;
        this.filterModel.workingOrder = null;
      }
      this.applyRelationshipFilters();
    };
    this.filterModel.makeCountryTeamQuery = (clear = false) => {
      this.filterModel.countryTeam = clear ? null : this.filterModel.workingCountryTeam;
      this.applyRelationshipFilters();
    };
    if (this.parentObject === 'Advertiser' && this.parentId) {
      this.filterModel.assignAdvertiser(this.parentId);
      this.filterModel.blockTeams();
    }
  }

  private applyDateFilter(clear: boolean): void {
    if (clear) {
      this.filterModel.startDateFilter = {dir: '>=', startDate: null, workingStartDate: null};
      this.filterModel.endDateFilter = {dir: '<=', endDate: null, workingEndDate: null};
    } else {
      this.filterModel.startDateFilter.startDate = this.filterModel.startDateFilter.workingStartDate;
      this.filterModel.endDateFilter.endDate = this.filterModel.endDateFilter.workingEndDate;
    }
    const conditions: string[] = [];
    const addDate = (field: 'startDate' | 'endDate', filter: any) => {
      if (!filter[field]) {
        return;
      }
      const dir = filter.dir.includes('=') ? filter.dir : filter.dir + '=';
      const boundary = dir.includes('>') ? 'start' : 'end';
      const instant = apiInstantFromPickerDateBoundary(filter[field], this.adnTimeShifter, boundary);
      if (instant) {
        conditions.push(`lineItem.${field}${dir}${instant.toISOString()}`);
      }
    };
    addDate('startDate', this.filterModel.startDateFilter);
    addDate('endDate', this.filterModel.endDateFilter);
    this.list.setFilterParams('dates', conditions.length ? {where: conditions.join(';')} : {});
  }

  private applyRelationshipFilters(): void {
    this.list.setFilterParams('relationships', {
      ...(this.filterModel.order?.id ? {order: this.filterModel.order.id} : {}),
      ...(this.filterModel.team?.id ? {team: this.filterModel.team.id} : {}),
      ...(this.filterModel.countryTeam ? {teamCountry: this.filterModel.countryTeam} : {})
    });
  }

  private normalizeColumns(fields: any[]): SignalAdminListColumn[] {
    return fields.map((field) => typeof field === 'string' ? {param: field} : {
      param: field.param ?? field.label ?? '',
      label: field.label,
      labelInstant: field.labelInstant,
      numeric: field.numeric,
      sortable: field.sortable
    });
  }

  private fieldId(field: any): string {
    return typeof field === 'string' ? field : field.param || field.label || field;
  }
}
